import {TEMP_SUB_ISSUE_ID_PREFIX} from '@github-ui/issue-create/values'
import type {OnDropArgs} from '@github-ui/drag-and-drop'
import {type EntityFieldValuesDeltaApplier, OPTIMISTIC_KEY} from '@github-ui/optimistic-connection'

export const applyIssueDependenciesSummaryDelta: EntityFieldValuesDeltaApplier = (entity, operation) => {
  if (operation.field !== 'issueDependenciesSummary') return false
  const summary = entity.issueDependenciesSummary
  if (!summary || typeof summary !== 'object' || Array.isArray(summary)) return false

  let changed = false
  for (const [field, delta] of Object.entries(operation.deltas)) {
    const current = (summary as Record<string, unknown>)[field]
    if (typeof current !== 'number' || !Number.isFinite(delta)) continue
    ;(summary as Record<string, unknown>)[field] = Math.max(0, current + delta)
    changed = true
  }
  return changed
}

export const applySubIssuesSummaryDelta: EntityFieldValuesDeltaApplier = (entity, operation) => {
  if (operation.field !== 'subIssuesSummary') return false
  const summary = entity.subIssuesSummary
  if (!summary || typeof summary !== 'object' || Array.isArray(summary)) return false

  const {completed, total} = summary as Record<string, unknown>
  const completedDelta = operation.deltas.completed
  if (
    typeof completed !== 'number' ||
    typeof total !== 'number' ||
    total < 0 ||
    typeof completedDelta !== 'number' ||
    !Number.isFinite(completedDelta)
  ) {
    return false
  }
  ;(summary as Record<string, unknown>).completed = Math.max(0, Math.min(total, completed + completedDelta))
  return true
}

export const applyIssueSummaryDelta: EntityFieldValuesDeltaApplier = (entity, operation) =>
  applyIssueDependenciesSummaryDelta(entity, operation) || applySubIssuesSummaryDelta(entity, operation)

/** The overlay is available on every issue viewer route. */
export function isSubIssuesOptimisticOverlayEnabled(): boolean {
  return true
}

/** Whether `id` is the client-only id a pending create row carries until the server assigns one. */
export function isTempSubIssueId(id: string | null | undefined): boolean {
  return id != null && id.startsWith(TEMP_SUB_ISSUE_ID_PREFIX)
}

export type ReprioritizeSubIssueParams = {
  issueId: string
  dragIssueId: string
  dropIssueId: string
  isBefore: boolean
}

/**
 * Resolves a drop into the reprioritise input, or `null` when it must not reach the server: a
 * self-drop, or (overlay only) either endpoint being a temp row with no server id yet.
 */
export function resolveSubIssueReorder(
  parentIssueId: string,
  {dragMetadata, dropMetadata, isBefore}: OnDropArgs<string>,
  overlayEnabled: boolean,
): ReprioritizeSubIssueParams | null {
  if (dragMetadata.id === dropMetadata.id) return null
  if (overlayEnabled && (isTempSubIssueId(dragMetadata.id) || isTempSubIssueId(dropMetadata.id))) return null
  return {issueId: parentIssueId, dragIssueId: dragMetadata.id, dropIssueId: dropMetadata.id, isBefore}
}

/**
 * Whether a row is a just-confirmed optimistic create, so a click during replication lag retries
 * the Not Found instead of erroring. `hadOptimisticUpdate && isLast` is a Relay-path fallback — a
 * one-way latch that would mark the last row forever — so the overlay uses `OPTIMISTIC_KEY` instead.
 */
export function resolveFromOptimisticUpdate(
  issueNode: object,
  {
    overlayEnabled,
    hadOptimisticUpdate,
    isLast,
  }: {overlayEnabled: boolean; hadOptimisticUpdate: boolean; isLast: boolean},
): boolean {
  return OPTIMISTIC_KEY in issueNode || (!overlayEnabled && hadOptimisticUpdate && isLast)
}

/**
 * Moves `dragId` into `dropId`'s slot. Removes first, then indexes against the remaining array, so
 * the result matches the reprioritise mutation's before/after target. Unchanged if either id is absent.
 */
export function reorderNodes<T extends {id: string} | null | undefined>(
  nodes: readonly T[] | null,
  dragId: string,
  dropId: string,
  isBefore: boolean,
): readonly T[] | null {
  if (!nodes) return nodes
  // A self-move would remove the node and then index against its own absence, corrupting the order.
  if (dragId === dropId) return nodes
  const from = nodes.findIndex(node => node?.id === dragId)
  if (from === -1 || nodes.findIndex(node => node?.id === dropId) === -1) return nodes
  const next = [...nodes]
  const moved = next.splice(from, 1)
  const dropIndex = next.findIndex(node => node?.id === dropId)
  next.splice(isBefore ? dropIndex : dropIndex + 1, 0, ...moved)
  return next
}
